import { useState } from 'react';
import type { AppUpdate, AppUpdateArtifact, DeviceModel } from '../../services/admin';
import {
  artifactStatusView,
  buildTargetView,
  canonicalUploadModels,
  formatBytes,
  formatTimestamp,
  modelOptionLabel,
  shortFingerprint,
  signerPolicyLabel,
} from './apkArtifact';

// APK Repository building blocks (Phase C3.5): read-only views of the artifact evidence the server
// recorded, the upload form (administrator-controlled values only) and the per-release actions.
// No data fetching here, so each piece renders from props alone.

const STATUS_TONE: Record<string, string> = {
  VALIDATED: 'bg-green-900/50 text-green-300',
  LEGACY: 'bg-blue-900/50 text-blue-300',
  PENDING_INSPECTION: 'bg-yellow-900/50 text-yellow-300',
  UNKNOWN: 'bg-gray-700 text-gray-300',
};

export function ArtifactStatusBadge({ artifact }: { artifact?: AppUpdateArtifact }) {
  const view = artifactStatusView(artifact?.status);
  return (
    <span data-artifact-status={view.kind} title={view.description} className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${STATUS_TONE[view.kind]}`}>
      {view.label}
    </span>
  );
}

// Install-compatibility signer only: which certificate the model's platform accepts. Never
// presented as a statement about who built the APK.
export function SignerSummary({ artifact }: { artifact?: AppUpdateArtifact }) {
  const fp = artifact?.signer_cert_sha256?.toLowerCase();
  if (!fp) return <span className="text-gray-500">Not recorded</span>;
  const policy = signerPolicyLabel(artifact?.signer_policy_status);
  return (
    <div data-signer className="space-y-1">
      <div>
        Recorded signer: <span className="font-mono" title={fp}>{shortFingerprint(fp)}</span>
      </div>
      {policy && <div className="text-gray-400">Current policy: {policy}</div>}
      <details>
        <summary className="cursor-pointer text-blue-400 hover:text-blue-300">Full SHA-256 fingerprint</summary>
        <code data-signer-full className="block break-all select-all font-mono text-xs text-gray-300 mt-1">{fp}</code>
      </details>
      <div className="text-xs text-gray-500">Signer policy covers install compatibility only.</div>
    </div>
  );
}

function BuildTarget({ artifact }: { artifact?: AppUpdateArtifact }) {
  const view = buildTargetView(artifact);
  if (view.kind === 'declared') {
    return (
      <span data-build-target="declared">
        <span className="font-mono">{view.target}</span>
        {view.schema && <span className="text-gray-400"> · Schema {view.schema}</span>}
      </span>
    );
  }
  return <span data-build-target={view.kind} className="text-gray-400">{view.text}</span>;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5">
      <dt className="text-gray-400">{label}</dt>
      <dd className="col-span-2 text-gray-200 break-words">{children}</dd>
    </div>
  );
}

// Every value comes from the server's record of the APK; "Assigned device model" is the catalog
// association and is deliberately kept apart from the artifact-declared build target.
export function ArtifactDetails({ update }: { update: AppUpdate }) {
  const a = update.artifact;
  const status = artifactStatusView(a?.status);
  const noEvidence = <span className="text-gray-500">Not recorded</span>;
  return (
    <dl data-artifact-details className="text-sm divide-y divide-gray-700/60">
      <Row label="Artifact status">
        <ArtifactStatusBadge artifact={a} />
        <div className="text-xs text-gray-400 mt-1">{status.description}</div>
      </Row>
      <Row label="Version">
        <span className="font-mono">{update.version_name}</span>
        <div className="text-xs text-gray-400">Version code {update.version_code}</div>
      </Row>
      <Row label="Package">{a?.package_name ? <span className="font-mono">{a.package_name}</span> : noEvidence}</Row>
      <Row label="Build target marker"><BuildTarget artifact={a} /></Row>
      <Row label="Assigned device model">{update.model_name ?? <span className="text-gray-500">—</span>}</Row>
      <Row label="Signer"><SignerSummary artifact={a} /></Row>
      <Row label="File size">{formatBytes(update.file_size_bytes)}</Row>
      <Row label="SHA-256">
        <code className="block break-all select-all font-mono text-xs">{update.sha256}</code>
      </Row>
      <Row label="Inspected">
        {a?.checked_at ? formatTimestamp(a.checked_at) : noEvidence}
        {a?.validator_version && (
          <div className="text-xs text-gray-500">
            {a.validator_version}
            {a.validator_contract_version && ` · contract ${a.validator_contract_version}`}
          </div>
        )}
      </Row>
    </dl>
  );
}

export function PublishResult({ update, onClose }: { update: AppUpdate; onClose: () => void }) {
  return (
    <div data-publish-result className="space-y-4">
      <h3 className="text-2xl font-bold text-white">APK published</h3>
      <p className="text-sm text-gray-300">
        The server read these values from the APK itself. They were not entered by an administrator.
        {update.is_active ? ' This release is now the current release for its model.' : ' The current release for this model is unchanged.'}
      </p>
      <ArtifactDetails update={update} />
      <div className="flex justify-end">
        <button type="button" onClick={onClose} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded">
          Close
        </button>
      </div>
    </div>
  );
}

export interface UploadRequest {
  deviceModelId: string;
  releaseNotes?: string;
  file: File;
}

const inputClass =
  'w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50';

// Only administrator-controlled values: the canonical model, the APK and optional notes. Version,
// package, signer, build target, hash and size are read from the APK by the server.
export function UploadForm({
  models,
  uploading,
  error,
  onSubmit,
  onCancel,
}: {
  models: DeviceModel[];
  uploading: boolean;
  error?: string | null;
  onSubmit: (req: UploadRequest) => void;
  onCancel: () => void;
}) {
  const canonical = canonicalUploadModels(models);
  const hidden = models.length - canonical.length;
  const [deviceModelId, setDeviceModelId] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const noCanonical = canonical.length === 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (uploading || noCanonical) return;
    if (!deviceModelId) return setLocalError('Select a device model');
    if (!file) return setLocalError('Select an APK file');
    setLocalError(null);
    onSubmit({ deviceModelId, releaseNotes: releaseNotes.trim() || undefined, file });
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-upload-form>
      <p className="text-sm text-gray-400">
        Version, package, signer and build target are read from the APK by the server. APK upload size is limited by the server configuration.
      </p>
      {noCanonical && (
        <div data-no-canonical className="bg-yellow-900/30 border border-yellow-600 text-yellow-300 rounded p-3 text-sm">
          No canonical device model is available. A model needs a canonical manufacturer/model identity in the Model Catalog before APKs can be
          uploaded for it.
        </div>
      )}
      <fieldset disabled={uploading} className="space-y-4">
        <label className="block">
          <p className="text-sm font-semibold text-gray-300 mb-1">Device Model</p>
          <select
            name="device_model_id"
            value={deviceModelId}
            onChange={(e) => setDeviceModelId(e.target.value)}
            className={inputClass}
            disabled={noCanonical}
            required
          >
            <option value="">Select model…</option>
            {canonical.map((m) => (
              <option key={m.id} value={m.id}>
                {modelOptionLabel(m)}
              </option>
            ))}
          </select>
          {hidden > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {hidden} provisional model{hidden === 1 ? ' is' : 's are'} not listed: {hidden === 1 ? 'it has' : 'they have'} no canonical identity.
            </p>
          )}
        </label>

        <label className="block">
          <p className="text-sm font-semibold text-gray-300 mb-1">APK File</p>
          <input
            name="file"
            type="file"
            accept=".apk"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:font-semibold hover:file:bg-blue-700"
            required
          />
        </label>

        <label className="block">
          <p className="text-sm font-semibold text-gray-300 mb-1">Release Notes</p>
          <textarea
            name="release_notes"
            value={releaseNotes}
            onChange={(e) => setReleaseNotes(e.target.value)}
            rows={3}
            className={inputClass}
            placeholder="Optional"
          />
        </label>
      </fieldset>

      {(localError || error) && (
        <div data-upload-error className="bg-red-900/30 border border-red-600 text-red-300 rounded p-3 text-sm">
          {localError || error}
        </div>
      )}
      {uploading && (
        <div data-uploading className="flex items-center gap-3 text-sm text-blue-300">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
          Uploading and validating the APK. This can take a few minutes; keep this window open.
        </div>
      )}

      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploading}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={uploading || noCanonical}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
        >
          {uploading ? 'Uploading & validating…' : 'Upload and publish'}
        </button>
      </div>
    </form>
  );
}

// Unchanged activation behaviour: the active release offers "Update ALL", any other release -
// whatever its artifact status - offers "Set as Current". Enforcement is the backend's job.
export function ReleaseActions({
  update,
  downloadUrl,
  pushing,
  settingActive,
  onPush,
  onSetActive,
  onDelete,
}: {
  update: AppUpdate;
  downloadUrl: string;
  pushing: boolean;
  settingActive: boolean;
  onPush: () => void;
  onSetActive: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <a href={downloadUrl} className="text-blue-400 hover:text-blue-300 font-semibold mr-3">
        Download
      </a>
      {update.is_active ? (
        <button onClick={onPush} disabled={!update.model_name || pushing} className="text-green-400 hover:text-green-300 font-semibold mr-3 disabled:opacity-50">
          {pushing ? 'Updating...' : 'Update ALL'}
        </button>
      ) : (
        <button onClick={onSetActive} disabled={settingActive} className="text-yellow-400 hover:text-yellow-300 font-semibold mr-3 disabled:opacity-50">
          {settingActive ? 'Updating...' : 'Set as Current'}
        </button>
      )}
      <button onClick={onDelete} className="text-red-400 hover:text-red-300 font-semibold">
        Delete
      </button>
    </>
  );
}
